import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, MessageSquare, Clock, CheckCircle2, AlertTriangle, Search,
  Loader2, ChevronRight, Send, X, Filter, Tag, User, ArrowLeft,
  LifeBuoy, Inbox, AlertCircle, XCircle, ChevronDown, Trash2, MoreHorizontal,
  BarChart3, TrendingUp, UserCheck, RefreshCw, PieChart, Activity,
  ArrowUpCircle, Building2, UserCog
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RePieChart, Pie, Cell, LineChart, Line, Legend
} from "recharts";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";

const STATUS_MAP = {
  open: { label: "Open", icon: Inbox, color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200" },
  in_progress: { label: "In Progress", icon: Clock, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
  waiting_on_customer: { label: "Waiting", icon: AlertCircle, color: "text-purple-700", bg: "bg-purple-50", border: "border-purple-200" },
  resolved: { label: "Resolved", icon: CheckCircle2, color: "text-green-700", bg: "bg-green-50", border: "border-green-200" },
  closed: { label: "Closed", icon: XCircle, color: "text-slate-700", bg: "bg-slate-50", border: "border-slate-200" },
};

const PRIORITY_MAP = {
  low: { label: "Low", color: "text-slate-600", bg: "bg-slate-50", border: "border-slate-200" },
  medium: { label: "Medium", color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" },
  high: { label: "High", color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200" },
  urgent: { label: "Urgent", color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
};

const CATEGORY_MAP = {
  general: "General", technical: "Technical", billing: "Billing",
  compliance: "Compliance", feature_request: "Feature Request",
};

const PIE_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

const SupportPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSuperAdmin = user?.role === "super_admin";
  const [activeView, setActiveView] = useState("tickets");
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [commentSaving, setCommentSaving] = useState(false);
  const [createForm, setCreateForm] = useState({ subject: "", description: "", category: "general", priority: "medium" });
  const [showEscalate, setShowEscalate] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [escalateForm, setEscalateForm] = useState({ escalation_type: "department", department: "", person_id: "", person_name: "", reason: "", priority: "" });
  const [deleteTicketTarget, setDeleteTicketTarget] = useState(null);
  const [deleteTicketConfirm, setDeleteTicketConfirm] = useState("");

  const fetchTickets = useCallback(async () => {
    try {
      const [tRes, sRes] = await Promise.all([
        api.get("/support/tickets"),
        api.get("/support/tickets/stats"),
      ]);
      setTickets(tRes.data || []);
      setStats(sRes.data);
    } catch {}
    setLoading(false);
  }, []);

  const fetchAnalytics = useCallback(async () => {
    if (!isSuperAdmin) return;
    setAnalyticsLoading(true);
    try {
      const res = await api.get("/support/analytics");
      setAnalytics(res.data);
    } catch {}
    setAnalyticsLoading(false);
  }, [isSuperAdmin]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  // Auto-open ticket from ?ticket= query param
  useEffect(() => {
    const ticketId = searchParams.get("ticket");
    if (ticketId && tickets.length > 0 && !selectedTicket) {
      const target = tickets.find(t => t.id === ticketId);
      if (target) {
        openTicket(target);
        searchParams.delete("ticket");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [tickets, searchParams, selectedTicket, setSearchParams]); // eslint-disable-line
  useEffect(() => { if (activeView === "analytics") fetchAnalytics(); }, [activeView, fetchAnalytics]);

  const handleCreate = async () => {
    if (!createForm.subject || !createForm.description) { toast.error("Subject and description required"); return; }
    setSaving(true);
    try {
      await api.post("/support/tickets", createForm);
      toast.success("Ticket created");
      setShowCreate(false);
      setCreateForm({ subject: "", description: "", category: "general", priority: "medium" });
      fetchTickets();
    } catch (e) { toast.error("Failed to create ticket"); }
    setSaving(false);
  };

  const openTicket = async (ticket) => {
    setDetailLoading(true);
    setSelectedTicket(ticket);
    try {
      const res = await api.get(`/support/tickets/${ticket.id}`);
      setSelectedTicket(res.data);
    } catch { toast.error("Failed to load ticket"); }
    setDetailLoading(false);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setCommentSaving(true);
    try {
      await api.post(`/support/tickets/${selectedTicket.id}/comments`, { content: newComment, is_internal: isInternal });
      toast.success("Reply added");
      setNewComment("");
      setIsInternal(false);
      openTicket(selectedTicket);
      fetchTickets();
    } catch { toast.error("Failed"); }
    setCommentSaving(false);
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await api.put(`/support/tickets/${ticketId}`, { status: newStatus });
      toast.success("Status updated");
      if (selectedTicket?.id === ticketId) openTicket({ ...selectedTicket, status: newStatus });
      fetchTickets();
    } catch { toast.error("Failed"); }
  };

  const handlePriorityChange = async (ticketId, newPriority) => {
    try {
      await api.put(`/support/tickets/${ticketId}`, { priority: newPriority });
      toast.success("Priority updated");
      fetchTickets();
    } catch { toast.error("Failed"); }
  };

  const handleDelete = async (ticketId) => {
    try {
      await api.delete(`/support/tickets/${ticketId}`);
      toast.success("Ticket deleted");
      if (selectedTicket?.id === ticketId) setSelectedTicket(null);
      setDeleteTicketTarget(null);
      setDeleteTicketConfirm("");
      fetchTickets();
    } catch { toast.error("Failed"); }
  };

  const openEscalateDialog = async () => {
    setShowEscalate(true);
    setEscalateForm({ escalation_type: "department", department: "", person_id: "", person_name: "", reason: "", priority: "" });
    try {
      const [deptRes, staffRes] = await Promise.all([
        api.get("/support/departments"),
        isSuperAdmin ? api.get("/admin/platform-staff") : Promise.resolve({ data: { staff: [] } }),
      ]);
      setDepartments(deptRes.data?.departments || []);
      setStaffList(staffRes.data?.staff || []);
    } catch { /* silent */ }
  };

  const handleEscalate = async () => {
    if (!escalateForm.department && !escalateForm.person_id) {
      toast.error("Select a department or person to escalate to");
      return;
    }
    try {
      await api.post(`/support/tickets/${selectedTicket.id}/escalate`, escalateForm);
      toast.success("Ticket escalated");
      setShowEscalate(false);
      openTicket(selectedTicket);
      fetchTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Escalation failed");
    }
  };

  const filteredTickets = tickets.filter(t => {
    if (activeTab !== "all" && t.status !== activeTab) return false;
    if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
    if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (t.subject || "").toLowerCase().includes(q) || (t.created_by_name || "").toLowerCase().includes(q);
    }
    return true;
  });

  const variant = user?.role === "super_admin" ? "super_admin" : user?.role === "tenant_admin" ? "tenant_admin" : user?.role === "manager" ? "manager" : "learner";
  const s = stats || { total: 0, open: 0, in_progress: 0, resolved: 0, waiting: 0, urgent: 0 };

  // ═══════════════ TICKET DETAIL VIEW ═══════════════
  if (selectedTicket) {
    const st = STATUS_MAP[selectedTicket.status] || STATUS_MAP.open;
    const pr = PRIORITY_MAP[selectedTicket.priority] || PRIORITY_MAP.medium;
    return (
      <AdminLayout title="Support" variant={variant}>
        <div className="max-w-4xl space-y-4" data-testid="ticket-detail">
          <Button variant="ghost" size="sm" className="text-xs gap-1 -ml-2" onClick={() => setSelectedTicket(null)} data-testid="back-to-tickets">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Tickets
          </Button>

          <Card className="border shadow-none">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-bold" data-testid="ticket-subject">{selectedTicket.subject}</h2>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <Badge className={cn("text-[9px] px-1.5 py-0 border", st.bg, st.color, st.border)}>{st.label}</Badge>
                    <Badge className={cn("text-[9px] px-1.5 py-0 border", pr.bg, pr.color, pr.border)}>{pr.label}</Badge>
                    <Badge variant="outline" className="text-[9px]">{CATEGORY_MAP[selectedTicket.category] || selectedTicket.category}</Badge>
                    {selectedTicket.escalated && <Badge className="text-[9px] px-1.5 py-0 bg-orange-100 text-orange-700 border-orange-200">Escalated</Badge>}
                    <span className="text-[10px] text-muted-foreground">by {selectedTicket.created_by_name} &middot; {selectedTicket.created_at ? new Date(selectedTicket.created_at).toLocaleDateString() : ""}</span>
                  </div>
                  {/* Escalation Info */}
                  {selectedTicket.escalated && (
                    <div className="mt-2 px-3 py-2 rounded-lg bg-orange-50 border border-orange-200 text-xs">
                      <span className="font-semibold text-orange-700">Escalated to: </span>
                      {selectedTicket.escalation_department && <span className="text-orange-600">{selectedTicket.escalation_department}</span>}
                      {selectedTicket.escalation_department && selectedTicket.escalation_person_name && <span className="text-muted-foreground"> &middot; </span>}
                      {selectedTicket.escalation_person_name && <span className="text-orange-600">{selectedTicket.escalation_person_name}</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {isSuperAdmin && (
                    <>
                      <Button variant="outline" size="sm" className="h-8 text-xs gap-1 text-orange-600 border-orange-200 hover:bg-orange-50" onClick={openEscalateDialog} data-testid="escalate-ticket-btn">
                        <ArrowUpCircle className="w-3.5 h-3.5" /> Escalate
                      </Button>
                      <Select value="" onValueChange={v => handleStatusChange(selectedTicket.id, v)}>
                        <SelectTrigger className="h-8 text-xs w-36" data-testid="ticket-status-select"><SelectValue placeholder="Change Status" /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => { setDeleteTicketTarget(selectedTicket); setDeleteTicketConfirm(""); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </>
                  )}
                </div>
              </div>
              <div className="mt-4 p-4 rounded-lg bg-muted/30 border">
                <p className="text-sm whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>
            </CardContent>
          </Card>

          {/* Escalation History */}
          {(selectedTicket.escalation_history || []).length > 0 && (
            <Card className="border border-orange-200 shadow-none bg-orange-50/30">
              <CardHeader className="pt-3 pb-2 px-5"><CardTitle className="text-xs text-orange-700 flex items-center gap-2"><ArrowUpCircle className="w-3.5 h-3.5" /> Escalation History</CardTitle></CardHeader>
              <CardContent className="px-5 pb-4 space-y-2">
                {(selectedTicket.escalation_history || []).map((esc, i) => (
                  <div key={esc.id || i} className="flex items-start gap-3 p-2.5 rounded-lg border border-orange-200 bg-white">
                    <ArrowUpCircle className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {esc.department && <Badge className="text-[8px] px-1 py-0 bg-orange-100 text-orange-700 border-orange-200"><Building2 className="w-2.5 h-2.5 mr-0.5 inline" />{esc.department}</Badge>}
                        {esc.person_name && <Badge className="text-[8px] px-1 py-0 bg-violet-100 text-violet-700 border-violet-200"><UserCog className="w-2.5 h-2.5 mr-0.5 inline" />{esc.person_name}</Badge>}
                        <span className="text-[10px] text-muted-foreground">by {esc.escalated_by_name} &middot; {esc.escalated_at ? new Date(esc.escalated_at).toLocaleString() : ""}</span>
                      </div>
                      {esc.reason && <p className="text-xs text-muted-foreground mt-1">{esc.reason}</p>}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Comments Thread */}
          <Card className="border shadow-none">
            <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Conversation ({(selectedTicket.comments || []).length})</CardTitle></CardHeader>
            <CardContent className="px-5 pb-5 space-y-3">
              {detailLoading ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div> : (
                <>
                  {(selectedTicket.comments || []).length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No replies yet</p>}
                  {(selectedTicket.comments || []).map(c => (
                    <div key={c.id} className={cn("p-3 rounded-lg border", c.is_internal ? "bg-amber-50/30 border-amber-200" : c.author_role === "super_admin" ? "bg-primary/5 border-primary/20" : "bg-muted/20")}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs font-semibold">{c.author_name}</span>
                        <Badge variant="outline" className="text-[8px] px-1 py-0">{c.author_role?.replace("_", " ")}</Badge>
                        {c.is_internal && <Badge className="text-[8px] px-1 py-0 bg-amber-100 text-amber-700 border-amber-200">Internal Note</Badge>}
                        <span className="text-[10px] text-muted-foreground ml-auto">{c.created_at ? new Date(c.created_at).toLocaleString() : ""}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{c.content}</p>
                    </div>
                  ))}
                  {/* Reply Box */}
                  <div className="pt-2 border-t space-y-2">
                    <Textarea value={newComment} onChange={e => setNewComment(e.target.value)} placeholder={isSuperAdmin ? "Type your response..." : "Add a reply..."} rows={3} className="text-sm" data-testid="comment-input" />
                    <div className="flex items-center justify-between">
                      {isSuperAdmin && (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} className="rounded w-3.5 h-3.5" />
                          <span className="text-xs text-muted-foreground">Internal note (not visible to requester)</span>
                        </label>
                      )}
                      <div className="ml-auto" />
                      <Button size="sm" className="text-xs gap-1" onClick={handleAddComment} disabled={commentSaving || !newComment.trim()} data-testid="send-reply-btn">
                        {commentSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send Reply
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Delete Ticket Confirmation Dialog */}
        <Dialog open={!!deleteTicketTarget} onOpenChange={() => { setDeleteTicketTarget(null); setDeleteTicketConfirm(""); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="w-5 h-5" /> Delete Ticket</DialogTitle>
              <DialogDescription>
                This will permanently delete "<span className="font-semibold">{deleteTicketTarget?.subject}</span>" and all its comments. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-xs text-muted-foreground">Type <span className="font-mono font-bold text-foreground">DELETE</span> to confirm:</p>
              <Input value={deleteTicketConfirm} onChange={e => setDeleteTicketConfirm(e.target.value)}
                placeholder="Type DELETE" className="h-9 text-sm font-mono" data-testid="delete-ticket-confirm-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => { setDeleteTicketTarget(null); setDeleteTicketConfirm(""); }}>Cancel</Button>
              <Button size="sm" variant="destructive" disabled={deleteTicketConfirm !== "DELETE"} onClick={() => handleDelete(deleteTicketTarget.id)} data-testid="confirm-delete-ticket-btn">
                Delete Ticket
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Escalation Dialog */}
        <Dialog open={showEscalate} onOpenChange={setShowEscalate}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><ArrowUpCircle className="w-5 h-5 text-orange-500" /> Escalate Ticket</DialogTitle>
              <DialogDescription>Route this ticket to the appropriate department or person</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Escalation Type */}
              <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-lg">
                {["department", "person", "both"].map(t => (
                  <button key={t} onClick={() => setEscalateForm({...escalateForm, escalation_type: t})}
                    className={cn("flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors capitalize",
                      escalateForm.escalation_type === t ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground")}
                    data-testid={`escalate-type-${t}`}
                  >{t === "both" ? "Both" : t === "department" ? "Department" : "Person"}</button>
                ))}
              </div>

              {/* Department Selector */}
              {(escalateForm.escalation_type === "department" || escalateForm.escalation_type === "both") && (
                <div>
                  <Label className="text-xs mb-1.5 block">Department</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {departments.map(dept => (
                      <button key={dept.id} onClick={() => setEscalateForm({...escalateForm, department: dept.name})}
                        className={cn("text-left p-2.5 rounded-lg border text-xs transition-all",
                          escalateForm.department === dept.name ? "border-orange-400 bg-orange-50 text-orange-700" : "border-border hover:border-orange-200"
                        )}
                        data-testid={`dept-${dept.id}`}
                      >
                        <p className="font-medium">{dept.name}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">{dept.description}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Person Selector */}
              {(escalateForm.escalation_type === "person" || escalateForm.escalation_type === "both") && (
                <div>
                  <Label className="text-xs mb-1.5 block">Assign to Person</Label>
                  <Select value={escalateForm.person_id} onValueChange={v => {
                    const person = staffList.find(s => s.id === v);
                    setEscalateForm({...escalateForm, person_id: v, person_name: person?.full_name || ""});
                  }}>
                    <SelectTrigger className="h-9 text-xs" data-testid="escalate-person-select"><SelectValue placeholder="Select staff member..." /></SelectTrigger>
                    <SelectContent>
                      {staffList.map(s => (
                        <SelectItem key={s.id} value={s.id} className="text-xs">
                          {s.full_name} — {s.platform_role || s.role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Priority Bump */}
              <div>
                <Label className="text-xs mb-1.5 block">Update Priority (optional)</Label>
                <Select value={escalateForm.priority} onValueChange={v => setEscalateForm({...escalateForm, priority: v})}>
                  <SelectTrigger className="h-9 text-xs" data-testid="escalate-priority-select"><SelectValue placeholder="Keep current priority" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="keep" className="text-xs">Keep Current</SelectItem>
                    {Object.entries(PRIORITY_MAP).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Reason */}
              <div>
                <Label className="text-xs mb-1.5 block">Reason for Escalation</Label>
                <Textarea value={escalateForm.reason} onChange={e => setEscalateForm({...escalateForm, reason: e.target.value})}
                  rows={3} className="text-sm" placeholder="Why is this being escalated?" data-testid="escalate-reason" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowEscalate(false)}>Cancel</Button>
              <Button size="sm" className="text-xs gap-1 bg-orange-500 hover:bg-orange-600" onClick={handleEscalate} data-testid="confirm-escalate-btn">
                <ArrowUpCircle className="w-3 h-3" /> Escalate Ticket
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Support & Tickets" variant={variant}>
      <div className="space-y-5 max-w-7xl" data-testid="support-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Support Center</h1>
            <p className="text-sm text-muted-foreground">Manage tickets, track SLAs, and monitor support performance</p>
          </div>
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <div className="flex items-center gap-1 border rounded-lg p-0.5 bg-muted/30">
                <button onClick={() => setActiveView("tickets")} className={cn("px-3 py-1.5 text-xs font-medium rounded-md transition-colors", activeView === "tickets" ? "bg-white shadow-sm" : "hover:bg-white/50")} data-testid="view-tickets-btn">
                  <MessageSquare className="w-3.5 h-3.5 inline mr-1" /> Tickets
                </button>
                <button onClick={() => setActiveView("analytics")} className={cn("px-3 py-1.5 text-xs font-medium rounded-md transition-colors", activeView === "analytics" ? "bg-white shadow-sm" : "hover:bg-white/50")} data-testid="view-analytics-btn">
                  <BarChart3 className="w-3.5 h-3.5 inline mr-1" /> Analytics
                </button>
              </div>
            )}
            <Button size="sm" className="h-8 text-xs gap-1" onClick={() => setShowCreate(true)} data-testid="create-ticket-btn">
              <Plus className="w-3.5 h-3.5" /> New Ticket
            </Button>
          </div>
        </div>

        {/* ═══ ANALYTICS VIEW ═══ */}
        {activeView === "analytics" && isSuperAdmin && (
          <div className="space-y-5" data-testid="support-analytics">
            {analyticsLoading ? <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div> : analytics ? (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {[
                    { l: "Total Tickets", v: analytics.kpis.total, icon: MessageSquare, c: "text-primary bg-primary/10" },
                    { l: "Open", v: analytics.kpis.open, icon: Inbox, c: "text-blue-600 bg-blue-50" },
                    { l: "Resolution Rate", v: `${analytics.kpis.resolution_rate}%`, icon: CheckCircle2, c: "text-green-600 bg-green-50" },
                    { l: "Avg Resolution", v: `${analytics.kpis.avg_resolution_hours}h`, icon: Clock, c: "text-amber-600 bg-amber-50" },
                    { l: "SLA Compliance", v: `${analytics.kpis.sla_compliance}%`, icon: Activity, c: "text-violet-600 bg-violet-50" },
                  ].map((k, i) => (
                    <Card key={i} className="border shadow-none"><CardContent className="p-4">
                      <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center mb-2", k.c.split(" ")[1])}><k.icon className={cn("w-4 h-4", k.c.split(" ")[0])} /></div>
                      <p className="text-xl font-bold">{k.v}</p>
                      <p className="text-[10px] text-muted-foreground">{k.l}</p>
                    </CardContent></Card>
                  ))}
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Ticket Trend */}
                  <Card className="border shadow-none">
                    <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Ticket Trend (30 Days)</CardTitle></CardHeader>
                    <CardContent className="px-5 pb-5">
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={analytics.trend}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="date" tickFormatter={v => v.slice(5)} tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip contentStyle={{ fontSize: 12 }} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Line type="monotone" dataKey="created" stroke="#3b82f6" strokeWidth={2} dot={false} name="Created" />
                          <Line type="monotone" dataKey="resolved" stroke="#10b981" strokeWidth={2} dot={false} name="Resolved" />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* By Category Pie */}
                  <Card className="border shadow-none">
                    <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">By Category</CardTitle></CardHeader>
                    <CardContent className="px-5 pb-5">
                      <ResponsiveContainer width="100%" height={220}>
                        <RePieChart>
                          <Pie data={analytics.by_category} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                            {analytics.by_category.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ fontSize: 12 }} />
                        </RePieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* By Priority Bar */}
                  <Card className="border shadow-none">
                    <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">By Priority</CardTitle></CardHeader>
                    <CardContent className="px-5 pb-5">
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={analytics.by_priority}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip contentStyle={{ fontSize: 12 }} />
                          <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* By Status */}
                  <Card className="border shadow-none">
                    <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">By Status</CardTitle></CardHeader>
                    <CardContent className="px-5 pb-5">
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={analytics.by_status} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis type="number" tick={{ fontSize: 10 }} />
                          <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={90} />
                          <Tooltip contentStyle={{ fontSize: 12 }} />
                          <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                {/* Top Reporters */}
                {analytics.top_reporters?.length > 0 && (
                  <Card className="border shadow-none">
                    <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Top Reporters</CardTitle></CardHeader>
                    <CardContent className="px-5 pb-5">
                      <div className="space-y-2">
                        {analytics.top_reporters.map((r, i) => (
                          <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border">
                            <div className="flex items-center gap-3">
                              <span className="text-xs font-bold text-muted-foreground w-5">{i + 1}</span>
                              <span className="text-sm font-medium">{r.name}</span>
                            </div>
                            <Badge variant="outline" className="text-xs">{r.count} tickets</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : <p className="text-sm text-muted-foreground text-center py-12">No analytics data available</p>}
          </div>
        )}

        {/* ═══ TICKETS VIEW ═══ */}
        {activeView === "tickets" && (
          <div className="space-y-4">
            {/* Stats Strip */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {[
                { label: "Total", value: s.total, color: "text-primary" },
                { label: "Open", value: s.open, color: "text-blue-600" },
                { label: "In Progress", value: s.in_progress, color: "text-amber-600" },
                { label: "Resolved", value: s.resolved, color: "text-green-600" },
                { label: "Waiting", value: s.waiting, color: "text-purple-600" },
                { label: "Urgent", value: s.urgent, color: "text-red-600" },
              ].map((st, i) => (
                <div key={i} className="text-center p-2.5 rounded-lg border bg-muted/10">
                  <p className={cn("text-lg font-bold", st.color)}>{st.value}</p>
                  <p className="text-[10px] text-muted-foreground">{st.label}</p>
                </div>
              ))}
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
                <TabsList className="bg-muted/50 h-9">
                  <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
                  <TabsTrigger value="open" className="text-xs">Open</TabsTrigger>
                  <TabsTrigger value="in_progress" className="text-xs">In Progress</TabsTrigger>
                  <TabsTrigger value="resolved" className="text-xs">Resolved</TabsTrigger>
                  <TabsTrigger value="closed" className="text-xs">Closed</TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input placeholder="Search tickets..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-8 w-44 text-sm" data-testid="ticket-search" />
                </div>
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className="h-8 text-xs w-28" data-testid="priority-filter"><SelectValue placeholder="Priority" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" className="text-xs">All Priority</SelectItem>
                    {Object.entries(PRIORITY_MAP).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="h-8 text-xs w-32" data-testid="category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" className="text-xs">All Categories</SelectItem>
                    {Object.entries(CATEGORY_MAP).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Ticket List */}
            {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
              <div className="space-y-2" data-testid="tickets-list">
                {filteredTickets.length === 0 ? (
                  <Card className="border shadow-none"><CardContent className="py-12 text-center">
                    <LifeBuoy className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                    <p className="text-sm text-muted-foreground">No tickets match your filters</p>
                  </CardContent></Card>
                ) : filteredTickets.map(ticket => {
                  const st = STATUS_MAP[ticket.status] || STATUS_MAP.open;
                  const pr = PRIORITY_MAP[ticket.priority] || PRIORITY_MAP.medium;
                  const StIcon = st.icon;
                  return (
                    <Card key={ticket.id} className="border shadow-none hover:border-primary/30 transition-colors cursor-pointer group" onClick={() => openTicket(ticket)} data-testid={`ticket-${ticket.id}`}>
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", st.bg)}>
                          <StIcon className={cn("w-4 h-4", st.color)} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <h4 className="text-sm font-semibold group-hover:text-primary truncate">{ticket.subject}</h4>
                            <Badge className={cn("text-[8px] px-1 py-0 border shrink-0", st.bg, st.color, st.border)}>{st.label}</Badge>
                            <Badge className={cn("text-[8px] px-1 py-0 border shrink-0", pr.bg, pr.color, pr.border)}>{pr.label}</Badge>
                          </div>
                          <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                            <span>{ticket.created_by_name}</span>
                            <span>&middot;</span>
                            <span>{CATEGORY_MAP[ticket.category] || ticket.category}</span>
                            <span>&middot;</span>
                            <span>{ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : ""}</span>
                            {ticket.comment_count > 0 && <span className="flex items-center gap-0.5"><MessageSquare className="w-3 h-3" />{ticket.comment_count}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                          {isSuperAdmin && (
                            <>
                              <Select value="" onValueChange={v => handlePriorityChange(ticket.id, v)}>
                                <SelectTrigger className="h-7 text-[10px] w-20 border-dashed"><SelectValue placeholder="Priority" /></SelectTrigger>
                                <SelectContent>{Object.entries(PRIORITY_MAP).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>)}</SelectContent>
                              </Select>
                              <Select value="" onValueChange={v => handleStatusChange(ticket.id, v)}>
                                <SelectTrigger className="h-7 text-[10px] w-24 border-dashed"><SelectValue placeholder="Status" /></SelectTrigger>
                                <SelectContent>{Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>)}</SelectContent>
                              </Select>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={(e) => { e.stopPropagation(); setDeleteTicketTarget(ticket); setDeleteTicketConfirm(""); }}><Trash2 className="w-3 h-3" /></Button>
                            </>
                          )}
                          <ChevronRight className="w-4 h-4 text-muted-foreground/40" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Ticket Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
            <DialogDescription>Describe your issue and we'll get back to you</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Subject</Label><Input value={createForm.subject} onChange={e => setCreateForm({...createForm, subject: e.target.value})} className="h-8 text-sm" placeholder="Brief description of the issue" data-testid="ticket-subject-input" /></div>
            <div><Label className="text-xs">Description</Label><Textarea value={createForm.description} onChange={e => setCreateForm({...createForm, description: e.target.value})} rows={4} className="text-sm" placeholder="Provide details..." data-testid="ticket-description-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Category</Label>
                <Select value={createForm.category} onValueChange={v => setCreateForm({...createForm, category: v})}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CATEGORY_MAP).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Priority</Label>
                <Select value={createForm.priority} onValueChange={v => setCreateForm({...createForm, priority: v})}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(PRIORITY_MAP).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button size="sm" className="text-xs gap-1" onClick={handleCreate} disabled={saving} data-testid="submit-ticket-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Submit Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default SupportPage;
